import type { BroadcastChannel, BroadcastSenderMode } from '../types/broadcast.js';

// Stage 6A §6.3 — send-time validation (distinct from the lenient draft zod).
// Pure + shared: the /send endpoint enforces it, and the confirmation dialog
// surfaces the same errors before the AM commits.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type BroadcastSendValidationInput = {
  channels: BroadcastChannel[];
  emailSubject: string;
  emailBodyMarkdown: string;
  smsBody: string;
  emailFromMode: BroadcastSenderMode;
  emailFromCustomName: string | null;
  emailReplyToMode: BroadcastSenderMode;
  emailReplyToCustomEmail: string | null;
};

export type BroadcastReach = {
  emailReachable: number;
  smsReachable: number;
};

export function validateBroadcastForSend(
  input: BroadcastSendValidationInput,
  reach: BroadcastReach,
): string[] {
  const errors: string[] = [];
  const hasEmail = input.channels.includes('email');
  const hasSms = input.channels.includes('sms');

  if (!hasEmail && !hasSms) errors.push('Select at least one channel.');

  if (hasEmail) {
    if (!input.emailSubject.trim()) errors.push('Email subject is required.');
    if (!input.emailBodyMarkdown.trim()) errors.push('Email body is required.');
    if (input.emailFromMode === 'custom' && !(input.emailFromCustomName ?? '').trim()) {
      errors.push('A custom From name is required.');
    }
    if (input.emailReplyToMode === 'custom') {
      const email = (input.emailReplyToCustomEmail ?? '').trim();
      if (!email) errors.push('A custom Reply-To email is required.');
      else if (!EMAIL_RE.test(email)) errors.push('The custom Reply-To email is not valid.');
    }
  }

  if (hasSms && !input.smsBody.trim()) errors.push('SMS body is required.');

  // At least one actual send must result from the selected channels.
  const emailSends = hasEmail ? reach.emailReachable : 0;
  const smsSends = hasSms ? reach.smsReachable : 0;
  if (emailSends + smsSends === 0) {
    errors.push('No reachable recipients for the selected channel(s).');
  }

  return errors;
}
